
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Beaker, MapPin, Calendar, Package } from "lucide-react";
import { formatDate } from "@/utils/dateUtils";

interface ReagentCardProps {
  id: string;
  name: string;
  catalogNumber: string;
  category: string;
  description: string;
  amount: string;
  location?: string;
  city?: string;
  country?: string;
  imageUrl?: string;
  userName: string;
  labName: string;
  numberOfPackages?: string | number;
  expiryDate?: string | null;
}

const ReagentCard = ({
  id,
  name,
  catalogNumber,
  category,
  description,
  amount,
  location,
  city,
  country,
  imageUrl,
  userName,
  labName,
  numberOfPackages,
  expiryDate,
}: ReagentCardProps) => {
  console.log(`Rendering card for reagent: ${id} - ${name}`);
  
  const renderLocation = () => {
    if (city || country) {
      return [city, country].filter(Boolean).join(", ");
    }
    return location || "Location not specified";
  };
  
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <Link to={`/reagents/${id}`}>
        <div className="aspect-video relative overflow-hidden bg-gray-100">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={name}
              className="object-cover w-full h-full"
              onError={(e) => {
                console.log(`Image failed to load for ${name}`);
                // @ts-ignore
                e.target.style.display = 'none';
                // @ts-ignore
                e.target.parentElement.innerHTML = `<div class="flex items-center justify-center h-full bg-reagent-light-blue"><span class="lucide lucide-beaker h-16 w-16 text-reagent-blue opacity-50"></span></div>`;
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-reagent-light-blue">
              <Beaker className="h-16 w-16 text-reagent-blue opacity-50" />
            </div>
          )}
          <Badge className="absolute top-2 right-2 bg-reagent-blue">{category}</Badge>
        </div>
      </Link>
      
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold line-clamp-1">
          <Link to={`/reagents/${id}`} className="hover:text-reagent-blue transition-colors">
            {name}
          </Link>
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Catalog #: {catalogNumber || "N/A"}
        </p>
      </CardHeader>
      
      <CardContent className="pb-2">
        <p className="text-sm line-clamp-2 text-gray-700 mb-2">{description || "No description available"}</p>
        
        <div className="space-y-1 mt-2">
          <div className="flex items-center text-sm text-gray-500">
            <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
            <span className="truncate">{renderLocation()}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-sm flex items-center">
              <span className="font-medium mr-1">Amount:</span> {amount || "Not specified"}
            </p>
            
            {numberOfPackages && (
              <p className="text-sm flex items-center">
                <Package className="h-4 w-4 mr-1 inline flex-shrink-0" />
                <span>{numberOfPackages}</span>
              </p>
            )}
          </div>
          
          {expiryDate && (
            <p className="text-sm flex items-center">
              <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
              <span>Expires: {formatDate(expiryDate)}</span>
            </p>
          )}
        </div>
        
        <p className="text-sm mt-2 text-gray-600">
          Offered by: {userName || "Unknown"} {labName ? `(${labName})` : ""}
        </p>
      </CardContent>
      
      <CardFooter>
        <Button asChild className="w-full">
          <Link to={`/reagents/${id}`}>View Details</Link>
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ReagentCard;
